import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Order, OrderBranch, OrderBundle } from '../types';
import type { CatalogProduct } from '../lib/catalogTypes';
import { useCurrency } from '../hooks/useCurrency';
import { useProducts } from '../hooks/useCatalog';
import { useContactInfo } from '../hooks/usePublicContent';

const API_URL = import.meta.env.VITE_API_URL as string;

function imgSrc(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  return path.startsWith('http') ? path : `${API_URL}/storage/${path.replace(/^\/?(storage\/)?/, '')}`;
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_STEPS = [
  { key: 'pending',    label: 'Confirmed' },
  { key: 'processing', label: 'Preparing' },
  { key: 'framing',   label: 'Framing' },
  { key: 'shipped',   label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
] as const;

const STATUS_ORDER: Record<string, number> = {
  pending: 0, confirmed: 0,
  processing: 1, preparing: 1,
  framing: 2,
  shipped: 3,
  delivered: 4,
  cancelled: -1,
};

const PAYMENT_BADGES: Record<string, { label: string; cls: string }> = {
  cod:      { label: 'Cash on Delivery', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  cash:     { label: 'Cash on Delivery', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  card:     { label: 'Paid by Card',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  online:   { label: 'Paid Online',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  instapay: { label: 'Paid · InstaPay',  cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  vodafone: { label: 'Paid · Vodafone Cash', cls: 'bg-red-50 text-red-700 border-red-200' },
};

function getPaymentBadge(method: Order['payment_method'], status: string) {
  const code = (method?.code ?? 'cod').toLowerCase();
  const match = Object.entries(PAYMENT_BADGES).find(([k]) => code.includes(k));
  if (match) return match[1];
  if (status === 'delivered') return { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { label: method?.name ?? 'Cash on Delivery', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function estimatedDelivery(createdAt: string | null): string {
  const base = createdAt ? new Date(createdAt) : new Date();
  base.setDate(base.getDate() + 5);
  return base.toLocaleDateString('en-EG', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatAddress(addr: Record<string, string>): string[] {
  const parts: string[] = [];
  if (addr.address_line1) parts.push(addr.address_line1);
  if (addr.address_line2) parts.push(addr.address_line2);
  const city = [addr.area, addr.city].filter(Boolean).join(', ');
  if (city) parts.push(city);
  if (addr.country) parts.push(addr.country);
  return parts;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ProgressTimeline({ status }: { status: string }) {
  const step = STATUS_ORDER[status.toLowerCase()] ?? 0;
  if (step < 0) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-center text-sm font-medium text-red-600">
        This order was cancelled.
      </div>
    );
  }

  return (
    <div className="relative flex items-start justify-between gap-0">
      {/* connector track */}
      <div className="absolute top-4 left-0 right-0 h-px bg-stone-200 mx-[calc(100%/10)]" />
      <div
        className="absolute top-4 left-0 h-px bg-stone-900 transition-all duration-700"
        style={{
          left: `calc(100% / ${STATUS_STEPS.length * 2})`,
          width: step === 0 ? '0%' : `calc(${(step / (STATUS_STEPS.length - 1)) * 100}% - 100% / ${STATUS_STEPS.length})`,
        }}
      />

      {STATUS_STEPS.map((s, i) => {
        const done    = i < step;
        const current = i === step;
        return (
          <div key={s.key} className="relative z-10 flex flex-1 flex-col items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold transition-all ${
                done
                  ? 'border-stone-900 bg-stone-900 text-white'
                  : current
                  ? 'border-stone-900 bg-white text-stone-900 ring-4 ring-stone-100'
                  : 'border-stone-200 bg-white text-stone-300'
              }`}
            >
              {done ? (
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <span className={`text-center text-[11px] font-medium leading-tight ${
              current ? 'text-stone-900' : done ? 'text-stone-600' : 'text-stone-300'
            }`}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PricingRow({
  label, value, sub, highlight, strike,
}: {
  label: string; value: string; sub?: string; highlight?: boolean; strike?: boolean;
}) {
  return (
    <div className={`flex items-baseline justify-between gap-4 py-2 ${highlight ? 'border-t border-stone-200 mt-1 pt-3' : ''}`}>
      <span className={`text-sm ${highlight ? 'font-semibold text-stone-900' : 'text-stone-500'}`}>
        {label}
        {sub && <span className="ml-1.5 text-xs text-stone-400">{sub}</span>}
      </span>
      <span className={`text-sm whitespace-nowrap ${
        highlight ? 'text-base font-bold text-stone-900' : strike ? 'line-through text-stone-400' : 'text-stone-700'
      }`}>
        {value}
      </span>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function OrderConfirmationPage() {
  const { orderNumber = '' } = useParams();
  const { format } = useCurrency();

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['orders', orderNumber],
    queryFn: async () => (await api.get<Order>(`/orders/${orderNumber}`)).data,
    enabled: !!orderNumber,
  });

  const { data: allProducts } = useProducts();
  const { data: contactInfo } = useContactInfo();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 rounded-lg bg-stone-100" />
          <div className="h-4 w-72 rounded bg-stone-100" />
          <div className="h-40 rounded-2xl bg-stone-100" />
          <div className="h-64 rounded-2xl bg-stone-100" />
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-stone-100 text-2xl">
          🔍
        </div>
        <h1 className="font-serif text-2xl font-light text-stone-800">Order not found</h1>
        <p className="mt-3 text-sm text-stone-500">We couldn't find order {orderNumber}. Check the link or contact support.</p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-medium text-white hover:bg-stone-700">
          Back to shop
        </Link>
      </div>
    );
  }

  const isCancelled  = order.status.toLowerCase() === 'cancelled';
  const isPickup     = order.fulfillment_type === 'pickup';
  const payBadge     = getPaymentBadge(order.payment_method, order.status);
  const addressLines = formatAddress(order.shipping_address ?? {});

  const subtotal      = Number(order.subtotal);
  const discount      = Number(order.discount_total);
  const shipping      = Number(order.shipping_total);
  const tax           = Number(order.tax_total);
  const grandTotal    = Number(order.grand_total);
  const hasDiscount   = discount > 0;
  const hasTax        = tax > 0;
  const freeShipping  = shipping === 0;

  const trackingNumber = order.bosta_shipment?.tracking_number;

  // Recommendations — exclude products already in this order (items + bundles)
  const orderedProductIds = new Set([
    ...order.items.map((i) => i.product_id),
    ...order.bundles.flatMap((b) => b.products.map((p) => p.product_id)),
  ]);
  const recs = (allProducts ?? []).filter((p) => !orderedProductIds.has(p.id)).slice(0, 4);

  return (
    <div className="bg-stone-50 min-h-screen">
      {/* ── Top confirmation banner ─────────────────────────────────────── */}
      <div className="bg-stone-900 text-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 text-center">
          {!isCancelled && (
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/20 bg-white/10">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <p className="mb-1 text-xs font-medium uppercase tracking-[0.15em] text-stone-400">
            {isCancelled ? 'Order Cancelled' : 'Order Confirmed'}
          </p>
          <h1 className="font-serif text-3xl font-light leading-tight sm:text-4xl">
            {isCancelled ? 'Your order has been cancelled' : 'Thank you, it\'s being framed.'}
          </h1>
          <p className="mt-3 text-sm text-stone-400">
            <span className="font-mono text-stone-300">{order.order_number}</span>
            {order.customer_email && (
              <> · Confirmation sent to {order.customer_email}</>
            )}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 space-y-6">

        {/* ── Progress timeline ─────────────────────────────────────────── */}
        <section className="rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="mb-6 text-xs font-semibold uppercase tracking-widest text-stone-400">Order Progress</h2>
          <ProgressTimeline status={order.status} />
          <p className="mt-6 text-center text-xs text-stone-400">
            {isPickup ? 'Ready for pickup by ' : 'Estimated delivery: '}
            <span className="font-medium text-stone-700">{estimatedDelivery(order.created_at)}</span>
          </p>
        </section>

        {/* ── Order items ───────────────────────────────────────────────── */}
        <section className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="border-b border-stone-100 px-6 py-4">
            <h2 className="text-sm font-semibold text-stone-800">
              {order.items.length + order.bundles.length} {order.items.length + order.bundles.length === 1 ? 'Item' : 'Items'}
            </h2>
          </div>

          <div className="divide-y divide-stone-100">
            {order.items.map((item) => {
              const image = imgSrc(item.image_path);
              const isCustomPrint = (item.product_name ?? '').toLowerCase().includes('custom');
              const options = [
                item.size_label     && { label: 'Size',     value: item.size_label },
                item.frame_name     && { label: 'Frame',    value: item.frame_name },
                item.material_name  && { label: 'Material', value: item.material_name },
                ...(item.optional_services ?? []).map((s) => ({ label: 'Add-on', value: s })),
              ].filter(Boolean) as { label: string; value: string }[];

              return (
                <div key={item.id} className="flex gap-4 p-5">
                  {/* Thumbnail */}
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-stone-100 bg-stone-50">
                    {image ? (
                      <img src={image} alt={item.product_name ?? ''} className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-lg text-stone-200">🖼</div>
                    )}
                    {item.quantity > 1 && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-stone-800 text-[10px] font-bold text-white">
                        {item.quantity}
                      </span>
                    )}
                    {isCustomPrint && (
                      <span className="absolute bottom-0 left-0 right-0 bg-stone-800/70 py-0.5 text-center text-[8px] font-semibold uppercase tracking-wide text-white">
                        Custom
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-medium text-stone-900 leading-tight">
                        {item.product_name ?? `Product #${item.product_id}`}
                      </p>
                      <p className="shrink-0 text-sm font-semibold text-stone-900">
                        {format(Number(item.line_total))}
                      </p>
                    </div>

                    {options.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {options.map((o) => (
                          <span key={`${o.label}-${o.value}`}
                            className="rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] text-stone-600">
                            <span className="text-stone-400">{o.label}:</span> {o.value}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-3 text-xs text-stone-400">
                      <span>Qty {item.quantity}</span>
                      <span>·</span>
                      <span>{format(Number(item.unit_price))} each</span>
                    </div>

                    {isCustomPrint && (
                      <div className="mt-1 flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Custom artwork included — our team will review it shortly.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {order.bundles.map((bundle) => (
              <OrderBundleCard key={`bundle-${bundle.id}`} bundle={bundle} format={format} imgSrc={imgSrc} />
            ))}
          </div>
        </section>

        {/* ── Two-column: Pricing + Shipping ───────────────────────────── */}
        <div className="grid gap-6 sm:grid-cols-2">

          {/* Pricing breakdown */}
          <section className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-stone-400">Order Summary</h2>
            <div className="divide-y divide-stone-100">
              <PricingRow label="Subtotal" value={format(subtotal)} />
              {hasDiscount && (
                <PricingRow
                  label="Discount"
                  value={`−${format(discount)}`}
                  sub={order.coupon_code ? `· ${order.coupon_code}` : undefined}
                />
              )}
              <PricingRow
                label="Shipping"
                value={freeShipping ? 'Free' : format(shipping)}
              />
              {hasTax && <PricingRow label="Tax" value={format(tax)} />}
              <PricingRow label="Grand Total" value={format(grandTotal)} highlight />
            </div>

            {/* Payment status badge */}
            <div className={`mt-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${payBadge.cls}`}>
              <div className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
              {payBadge.label}
            </div>
          </section>

          {/* Shipping / Pickup & delivery */}
          <section className="rounded-2xl border border-stone-200 bg-white p-6 flex flex-col gap-5">
            {isPickup ? (
              <PickupSection branch={order.branch} customerName={order.customer_name} customerPhone={order.customer_phone} />
            ) : (
              <div>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone-400">Delivering To</h2>
                <p className="font-medium text-stone-800">{order.customer_name}</p>
                {addressLines.map((line, i) => (
                  <p key={i} className="text-sm text-stone-500 leading-relaxed">{line}</p>
                ))}
                {order.customer_phone && (
                  <p className="mt-1 text-sm text-stone-400">{order.customer_phone}</p>
                )}
              </div>
            )}

            <div className="border-t border-stone-100 pt-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone-400">
                {isPickup ? 'Ready For Pickup' : 'Estimated Arrival'}
              </h2>
              <p className="font-medium text-stone-800">{estimatedDelivery(order.created_at)}</p>
              <p className="mt-0.5 text-xs text-stone-400">Based on current processing time</p>
            </div>

            {trackingNumber && (
              <div className="border-t border-stone-100 pt-4">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone-400">Tracking</h2>
                <p className="font-mono text-sm font-medium text-stone-800">{trackingNumber}</p>
                <a
                  href={`https://app.bosta.co/tracking-shipment?trackingNumber=${trackingNumber}&lang=en`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-stone-600 hover:text-stone-900 underline underline-offset-2"
                >
                  Track on Bosta
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}
          </section>
        </div>

        {/* ── Action buttons ────────────────────────────────────────────── */}
        <section className="flex flex-wrap gap-3">
          {trackingNumber && (
            <a
              href={`https://app.bosta.co/tracking-shipment?trackingNumber=${trackingNumber}&lang=en`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-stone-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-stone-700 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Track Order
            </a>
          )}
          <Link
            to="/shop"
            className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors"
          >
            Continue Shopping
          </Link>
        </section>

        {/* ── Need to change something? ─────────────────────────────────── */}
        <section className="rounded-2xl border border-stone-200 bg-white p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-stone-400">
            Need to change something?
          </p>
          <p className="mb-4 text-sm text-stone-500 leading-relaxed">
            If you'd like to modify your order — size, frame, address, or anything else — reach out to us before it enters production.
          </p>
          <div className="flex flex-wrap gap-3">
            {contactInfo?.phone && (
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm font-medium text-stone-800 hover:border-stone-900 transition-colors"
              >
                <svg className="h-4 w-4 text-stone-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {contactInfo.phone}
              </a>
            )}
            {contactInfo?.email && (
              <a
                href={`mailto:${contactInfo.email}?subject=Order Change Request — ${order.order_number}`}
                className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm font-medium text-stone-800 hover:border-stone-900 transition-colors"
              >
                <svg className="h-4 w-4 text-stone-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {contactInfo.email}
              </a>
            )}
          </div>
        </section>

        {/* ── You May Also Like ─────────────────────────────────────────── */}
        {recs.length > 0 && (
          <section className="pt-4">
            <h2 className="mb-5 font-serif text-xl font-medium text-stone-800">You May Also Like</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {recs.map((product) => (
                <RecommendationCard key={product.id} product={product} format={format} />
              ))}
            </div>
          </section>
        )}

        <p className="pb-8 text-center text-xs text-stone-400">
          Galleria — Art, Framed With Intent
        </p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          header, footer, nav, .no-print { display: none !important; }
          body { background: white; }
          .rounded-2xl { border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function OrderBundleCard({
  bundle, format, imgSrc,
}: {
  bundle: OrderBundle;
  format: (v: number) => string;
  imgSrc: (path: string | null | undefined) => string | undefined;
}) {
  const image = imgSrc(bundle.bundle_image);
  const lineTotal = Number(bundle.line_total);
  const discountTotal = Number(bundle.discount_amount) * bundle.quantity;

  return (
    <div className="p-5">
      <div className="flex gap-4">
        {/* Bundle image */}
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-stone-100 bg-stone-50">
          {image ? (
            <img src={image} alt={bundle.bundle_name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-lg text-stone-200">📦</div>
          )}
          {bundle.quantity > 1 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-stone-800 text-[10px] font-bold text-white">
              {bundle.quantity}
            </span>
          )}
        </div>

        {/* Bundle details */}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-medium text-stone-900 leading-tight">{bundle.bundle_name}</p>
              <p className="text-xs text-stone-400">{bundle.products.length} {bundle.products.length === 1 ? 'product' : 'products'} included</p>
            </div>
            <p className="shrink-0 text-sm font-semibold text-stone-900">{format(lineTotal)}</p>
          </div>

          {/* Included products */}
          <div className="mt-1 space-y-1.5">
            {bundle.products.map((p, i) => {
              const pImg = imgSrc(p.product_image);
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md border border-stone-100 bg-stone-50">
                    {pImg ? (
                      <img src={pImg} alt={p.product_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-stone-200">🖼</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs font-medium text-stone-700">{p.product_name}</p>
                    <p className="truncate text-[11px] text-stone-400">
                      {[p.size_label, p.frame_name, p.material_name].filter(Boolean).join(' · ') || 'Standard'}
                    </p>
                  </div>
                  {p.quantity > 1 && <span className="text-[10px] text-stone-400">×{p.quantity}</span>}
                </div>
              );
            })}
          </div>

          {/* Bundle savings */}
          {discountTotal > 0 && (
            <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-1.5">
              <span className="text-[11px] text-emerald-700 font-medium">
                Bundle discount {bundle.discount_percent > 0 ? `(${Math.round(bundle.discount_percent)}%)` : ''}
              </span>
              <span className="text-[11px] font-semibold text-emerald-700">
                You saved {format(discountTotal)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickupSection({
  branch, customerName, customerPhone,
}: {
  branch: OrderBranch | null;
  customerName: string;
  customerPhone: string | null;
}) {
  return (
    <div>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-stone-400">Branch Pickup</h2>
      <p className="font-medium text-stone-800">{customerName}</p>
      {customerPhone && <p className="mt-0.5 text-sm text-stone-400">{customerPhone}</p>}

      {branch ? (
        <div className="mt-3 overflow-hidden rounded-xl border border-stone-200">
          {/* Map preview using Google Maps embed */}
          {branch.maps_url ? (
            <a
              href={branch.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative h-36 bg-stone-100 overflow-hidden group"
            >
              <iframe
                title="Branch location"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  [branch.name, branch.address, branch.city].filter(Boolean).join(', ')
                )}&output=embed&z=15`}
                className="w-full h-full border-0 pointer-events-none"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-end justify-end p-2 bg-gradient-to-t from-stone-900/40 to-transparent">
                <span className="flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-stone-800 shadow-sm group-hover:bg-white transition-colors">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open in Maps
                </span>
              </div>
            </a>
          ) : (
            <div className="h-36 bg-stone-50 flex items-center justify-center">
              <svg className="h-8 w-8 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          )}

          <div className="px-4 py-3 space-y-1">
            <p className="font-medium text-stone-800 text-sm">{branch.name}</p>
            {branch.address && <p className="text-xs text-stone-500">{branch.address}</p>}
            {branch.city && <p className="text-xs text-stone-400">{branch.city}</p>}
            {branch.phone && (
              <a href={`tel:${branch.phone}`} className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors pt-0.5">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {branch.phone}
              </a>
            )}
            {branch.maps_url && (
              <a
                href={branch.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors pt-0.5"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Get directions
              </a>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-stone-400">Branch details will be confirmed shortly.</p>
      )}
    </div>
  );
}

function RecommendationCard({
  product, format,
}: {
  product: CatalogProduct;
  format: (v: number) => string;
}) {
  const image = product.images?.[0]?.path;
  const imageSrc = image ? imgSrc(image) : undefined;
  const price = product.starting_price ?? Number(product.base_price);

  return (
    <Link to={`/products/${product.slug}`} className="group block">
      <div className="relative mb-2.5 aspect-square overflow-hidden rounded-xl bg-stone-100">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={product.name}
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl text-stone-200">🖼</div>
        )}
      </div>
      <p className="truncate text-sm font-medium text-stone-800 group-hover:text-stone-600 transition-colors">
        {product.name}
      </p>
      <p className="text-xs text-stone-500">{format(price)}</p>
    </Link>
  );
}
